import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { CLIENT_RATE_LIMITS } from "@/utils/dress-style-options";
import {
  updateJourneyStage,
  updateJourneyPath,
  getJourneyContext,
} from "@/utils/journeyTracking";
import { logger } from "@/utils/logger";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { redirectToLogin } from "@/hooks/useRedirectToLogin";
import { ensureAnonymousSession } from "@/lib/auth/anonymousSession";
import {
  generateFrontImage as generateFrontImageService,
  generateBackImage as generateBackImageService,
  inspectFrontImagePrompt,
  inspectBackImagePrompt,
  recoverPromptFromChat,
  InsufficientCreditsError,
  RateLimitError,
  GenerationInProgressError,
  ValidationError,
  isPromptValidationError,
  ServerError,
} from "@/services/imageGenerationService";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import { useCreatePageStore } from "@/stores/createPageStore";
import {
  createDesignSpecSnapshot,
  diffDesignSpecFields,
} from "@/lib/agent/designSpecSnapshot";
import {
  getSideRelevantDesignChanges,
  projectSavedBackStaleness,
} from "@/lib/designer/previewFreshness";
import { planDesignChanges } from "@/lib/designer/applyDesignChanges";
import { getBackDesignSpecForLatestFront } from "@/lib/designer/savedDesign";
import { compileCurrentDesignSpec } from "@/services/turn/brief/briefForm";
import {
  formatGenerationFailureMessage,
  type GenerationCreditRecovery,
} from "@/lib/generationFailureUx";
import type { DesignSpec } from "@/types/designSpec";
import type { DesignRenderExecutionOptions } from "@/lib/designer/applyDesignChanges";
import { prepareSavePointPayload } from "../utils/formStateHelpers";
import { ActionHookContext } from "./types";

const GENERIC_GENERATION_FAILURE_MESSAGE =
  "Something went wrong. Please try again when you're ready.";

// A generation request has no client-side abort, so a transport that never
// settles would leave the progress UI spinning with no way back. This window
// bounds that hang. It sits above /api/generate-image's 120s maxDuration so a
// render that can still legitimately arrive is never cut short — p95 renders
// land in 16-30s, well inside it.
export const GENERATION_WATCHDOG_TIMEOUT_MS = 150_000;

function isProjectActionCurrent(projectActionEpoch: number): boolean {
  return (
    useCreatePageStore.getState().projectActionEpoch === projectActionEpoch
  );
}

function shouldInspectGeneratePrompt(searchParams: URLSearchParams): boolean {
  if (searchParams.get("inspectGeneratePrompt") === "1") return true;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("inspectGeneratePrompt") === "1";
  } catch {
    return false;
  }
}

function formatPromptInspectionMessage(params: {
  side: "front" | "back";
  requestBody: Record<string, unknown>;
  systemInstruction: string;
  finalPrompt: string;
}): string {
  return [
    `Generate prompt inspection (${params.side}) - no image was generated.`,
    "",
    "Request body sent before prompt assembly:",
    "```json",
    JSON.stringify(params.requestBody, null, 2),
    "```",
    "",
    "Gemini system instruction:",
    "```text",
    params.systemInstruction,
    "```",
    "",
    "Gemini content prompt:",
    "```text",
    params.finalPrompt,
    "```",
  ].join("\n");
}

export type OnImageComplete = (
  side: "front" | "back",
  imageUrl: string,
  variationUrls: string[],
  isModification: boolean,
  imageReviewToken?: string
) => void;

export function useGenerationActions(
  context: ActionHookContext,
  updateGeneratedTicket: (
    ticketIdToUpdate: string,
    frontImageUrl: string,
    frontImagePath: string | null,
    backImageUrl?: string | null,
    backImagePath?: string | null,
    backPromptOverride?: string | null
  ) => Promise<void>,
  onImageComplete?: OnImageComplete | null
) {
  const {
    store,
    stateRef,
    session,
    isAnonymous,
    refreshSession,
    roles,
    rolesLoading,
    router,
    pathname,
    searchParams,
    isGeneratingRef,
    identityEpochRef,
    isChatMode: contextIsChatMode,
  } = context;

  // Get credit-related values from context
  const {
    refresh: invalidateCredits,
    hasSufficientCredits,
    isLoading: creditsLoading,
    isFetching: creditsFetching,
  } = useCreditsContext();

  // Surface a generation failure. A toast alone vanishes and strands the user
  // (a top production complaint — "it's a scam" after a silent failure). In chat
  // mode we also drop an assistant message into the transcript with a one-click
  // retry, so the failure is visible and recoverable in context.
  const reportGenerationFailure = useCallback(
    (
      side: "front" | "back",
      errorMessage: string,
      isChatMode?: boolean,
      // Some failures must NOT offer an immediate retry — e.g. a generation is
      // already in progress, so re-firing would just hit the same error.
      offerRetry: boolean = true,
      creditRecovery?: GenerationCreditRecovery
    ) => {
      const customerMessage = formatGenerationFailureMessage(
        errorMessage,
        creditRecovery
      );
      store.generateError(customerMessage);
      // A request can consume and then atomically restore a credit before its
      // error reaches the browser. Refresh after every failure so the visible
      // balance never remains stale, even for ambiguous network responses.
      invalidateCredits().catch(error => {
        logger.warn("Failed to refresh credits after generation failure", {
          component: "CreatePageActions",
          error: error instanceof Error ? error.message : String(error),
        });
      });
      if (isChatMode ?? contextIsChatMode) {
        store.addMessage({
          role: "assistant",
          content: customerMessage,
          ...(offerRetry && {
            presets: [
              {
                label: "Try again",
                action: "action",
                actionId:
                  side === "front"
                    ? "generate_dress_front"
                    : "generate_dress_back",
                data: {},
                actionData: { generateType: side },
              },
            ],
          }),
        });
      }
    },
    [store, contextIsChatMode, invalidateCredits]
  );

  const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRunRef = useRef(0);
  const projectActionEpoch = useCreatePageStore(
    current => current.projectActionEpoch
  );

  const clearGenerationWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  // A project restore/reset may not be able to abort its image transport. Move
  // the local run token and release the one-shot guard before the new canvas is
  // interactive; the retired promise must not own either UI state or watchdog.
  useLayoutEffect(() => {
    generationRunRef.current += 1;
    clearGenerationWatchdog();
    isGeneratingRef.current = false;
  }, [clearGenerationWatchdog, isGeneratingRef, projectActionEpoch]);

  // Arm a deadline on a dispatched render so a request that resolves to neither
  // success nor failure still lands the customer in the normal failure state
  // with its retry affordance, instead of an endless progress bar.
  const armGenerationWatchdog = useCallback(
    (
      side: "front" | "back",
      isChatMode: boolean | undefined,
      run: number,
      identityEpoch: number,
      owningProjectActionEpoch: number
    ) => {
      clearGenerationWatchdog();
      const armedStartTime =
        useCreatePageStore.getState().generation.progress.startTime;
      watchdogRef.current = setTimeout(() => {
        watchdogRef.current = null;
        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        ) {
          return;
        }
        const progress = useCreatePageStore.getState().generation.progress;
        // Success and failure both clear loading, and a newer turn owns the UI
        // once it has restamped startTime.
        if (!progress.loading || progress.startTime !== armedStartTime) {
          return;
        }
        // Recover first, log second: nothing in the reporting path should be
        // able to depend on telemetry succeeding. The in-flight promise never
        // settled, so the generateImages finally block never released this
        // guard — clear it here or the retry click is swallowed as a duplicate.
        isGeneratingRef.current = false;
        reportGenerationFailure(
          side,
          GENERIC_GENERATION_FAILURE_MESSAGE,
          isChatMode
        );
        logger.error("Generation stalled past its deadline", {
          component: "CreatePageActions",
          metadata: {
            side,
            timeoutMs: GENERATION_WATCHDOG_TIMEOUT_MS,
            isChatMode: !!(isChatMode ?? contextIsChatMode),
          },
        });
      }, GENERATION_WATCHDOG_TIMEOUT_MS);
    },
    [
      clearGenerationWatchdog,
      contextIsChatMode,
      identityEpochRef,
      isGeneratingRef,
      reportGenerationFailure,
    ]
  );

  useEffect(() => clearGenerationWatchdog, [clearGenerationWatchdog]);

  // Generate front image only (step 2)
  const generateFrontImage = useCallback(
    async (
      isChatMode?: boolean,
      isRetryAfterRecovery?: boolean,
      designSpecOverride?: DesignSpec,
      options?: DesignRenderExecutionOptions
    ) => {
      const identityEpoch = identityEpochRef.current;
      const run = generationRunRef.current;
      // Use getState() to read latest form data (e.g. after updateFormData in same tick)
      const currentState = useCreatePageStore.getState();
      const owningProjectActionEpoch = currentState.projectActionEpoch;
      try {
        const designSpec = createDesignSpecSnapshot(
          designSpecOverride ??
            compileCurrentDesignSpec(
              currentState.agent.designBrief,
              currentState.form.data,
              {
                hasFrontImage: Boolean(
                  currentState.generation.images.front.url
                ),
                hasBackImage: Boolean(currentState.generation.images.back.url),
              }
            )
        );
        const result = await generateFrontImageService({
          prompt: currentState.form.data.prompt,
          agentSessionId: currentState.agent.sessionId,
          designSpec: designSpec ?? undefined,
          preset_type: currentState.form.data.preset_type ?? undefined,
          color: currentState.form.data.color,
          colorHex: currentState.form.data.colorHex,
          length: currentState.form.data.length,
          sleeve: currentState.form.data.sleeve,
          sleeveAddons: currentState.form.data.sleeveAddons,
          neckline: currentState.form.data.neckline,
          waistline: currentState.form.data.waistline,
          silhouette: currentState.form.data.silhouette,
          fabric: currentState.form.data.fabric,
          isModificationMode: currentState.ui.modification.isModificationMode,
          uploadedImages: currentState.form.data.uploadedFrontImages || [],
          uploadedImageUrls:
            currentState.form.data.uploadedFrontImageUrls || [],
          isUploadPreset: currentState.form.data.isUploadPreset,
          enableCrazyMode: currentState.form.data.enableCrazyMode || false,
          originalPrompt: currentState.form.data.prompt || undefined,
          modestyPreference: currentState.form.data.modestyPreference,
        });
        const renderedDesignSpec = createDesignSpecSnapshot(
          result.designSpec ?? designSpec
        );

        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        ) {
          return;
        }

        // Convert variations to state format (remove ticketId from variations for state)
        const variations = result.variations.map(v => ({
          imageUrl: v.imageUrl,
          imagePath: v.imagePath,
        }));

        // Update state with variations and warnings
        store.generateFrontSuccess(
          {
            variations,
            designSpec: renderedDesignSpec,
            formUpdates: result.extractedAttributes,
            warnings: result.warnings || [],
            partial_failure: result.partialFailure || false,
          },
          isChatMode
        );

        // Show the new front render in the transcript immediately, decoupled
        // from the follow-up review turn. That review is a second agent call
        // that can fail (rate limit / network) and otherwise strand the image —
        // the top "I can't see the image" complaint in production. The agent
        // still adds its spoken review as a text-only follow-up.
        if (isChatMode && variations.length > 0) {
          const imageUrl = variations[0]?.imageUrl;
          if (imageUrl) {
            const variationUrls = variations.map(v => v.imageUrl);
            store.addMessage({
              role: "assistant",
              content: "",
              imageUrl,
              imageUrls: variationUrls,
              imageSide: "front",
              selectedIndex: 0,
            });
            if (onImageComplete && !options?.suppressAgentReview) {
              onImageComplete(
                "front",
                imageUrl,
                variationUrls,
                false,
                result.imageReviewToken
              );
            }
          }
        }

        // Store ticket IDs from API response - tickets are created server-side
        // Always store ticket IDs for all variations (modifications also create tickets)
        if (variations.length > 0) {
          for (let i = 0; i < result.variations.length; i++) {
            const ticketId = result.variations[i]?.ticketId;
            if (ticketId) {
              store.setVariationTicketId(i, ticketId);
              logger.info(`Ticket ID stored for variation ${i}`, {
                component: "CreatePageActions",
                metadata: {
                  ticketId,
                  variationIndex: i,
                  totalVariations: variations.length,
                },
              });
            } else {
              logger.warn(`No ticket ID returned for variation ${i}`, {
                component: "CreatePageActions",
                metadata: {
                  variationIndex: i,
                  totalVariations: variations.length,
                },
              });
            }
          }

          // Log complete variationTicketIds state after all tickets are stored
          const stateAfterStorage = stateRef.current;
          logger.info("Ticket ID storage complete, final state", {
            component: "CreatePageActions",
            metadata: {
              variationTicketIds: stateAfterStorage.ticket.variationTicketIds,
              variationTicketIdsEntries: Object.entries(
                stateAfterStorage.ticket.variationTicketIds || {}
              ).map(([idx, id]) => ({
                index: parseInt(idx),
                ticketId: id,
              })),
              totalStored: Object.keys(
                stateAfterStorage.ticket.variationTicketIds || {}
              ).length,
            },
          });

          // Set main ticketId from first variation (which is auto-selected).
          // Read it from the API response: stateRef.current has not been synced
          // with the Zustand writes above yet, so it still holds the previous
          // generation's ticket ids and would leave the main ticket pointing at
          // the design the user just replaced.
          const firstVariationTicketId = result.variations[0]?.ticketId;
          if (firstVariationTicketId) {
            store.setTicketId(firstVariationTicketId);
            logger.info("Set main ticketId from first variation", {
              component: "CreatePageActions",
              metadata: {
                ticketId: firstVariationTicketId,
                variationIndex: 0,
              },
            });
          }
        }

        // Refresh credits after successful generation to sync with server (credit was deducted)
        // This is the single source of truth update after generation
        invalidateCredits().catch(error => {
          logger.warn("Failed to refresh credits after front generation", {
            component: "CreatePageActions",
            error: error instanceof Error ? error.message : String(error),
          });
        });
      } catch (error) {
        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        )
          return;
        // Only prompt-field validation can be repaired from chat context.
        // Structural DesignSpec conflicts require customer clarification and
        // must not be retried with an unrelated prompt rewrite.
        const canRecover =
          isPromptValidationError(error) &&
          (isChatMode ?? contextIsChatMode) &&
          !isRetryAfterRecovery;

        if (canRecover) {
          const state = useCreatePageStore.getState();
          const messages = state.agent.conversation.messages;
          if (messages.length > 0) {
            try {
              const recoveredPrompt = await recoverPromptFromChat({
                messages: messages.map(m => ({
                  role: m.role,
                  content: m.content,
                })),
                formState: state.form.data,
                side: "front",
              });
              if (
                generationRunRef.current !== run ||
                identityEpochRef.current !== identityEpoch ||
                !isProjectActionCurrent(owningProjectActionEpoch)
              ) {
                return;
              }
              store.updateFormData({ prompt: recoveredPrompt });
              logger.info(
                "Recovered prompt from chat, retrying front generation",
                {
                  component: "CreatePageActions",
                  metadata: { promptLength: recoveredPrompt.length },
                }
              );
              return generateFrontImage(
                isChatMode,
                true,
                designSpecOverride,
                options
              );
            } catch (recoveryError) {
              logger.warn("Prompt recovery failed, showing validation error", {
                component: "CreatePageActions",
                metadata: {
                  error:
                    recoveryError instanceof Error
                      ? recoveryError.message
                      : String(recoveryError),
                },
              });
            }
          }
        }

        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        ) {
          return;
        }

        // Handle service errors
        if (error instanceof InsufficientCreditsError) {
          // The server refused this render, so it must not start a cooldown.
          // A dismissed credit wall should reopen on the next Generate tap.
          if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.removeItem("lastGenerationTime");
          }
          // Insufficient credits: revert to previous step and show the wall the
          // server asked for. A visitor without an account has just watched
          // their free render get refused — showing them a plain top-up modal
          // would ask for money from someone who has no account to put it on.
          const stepAtWall = useCreatePageStore.getState().form.currentStep;
          store.setStep(1);
          store.setLoading(false);
          store.setCreditModalDismissed(false);
          store.setShowCreditModal(
            error.wall === "anonymous_free_render_spent"
              ? "anon_unlock"
              : "generate_front",
            stepAtWall
          );
          // Refresh credits to sync with server state
          invalidateCredits().catch(() => {});
          return;
        } else if (error instanceof RateLimitError) {
          const errorMessage = error.retryAfter
            ? `${error.message} Try again in ${error.retryAfter} seconds.`
            : error.message;
          reportGenerationFailure("front", errorMessage, isChatMode);
          return;
        } else if (error instanceof GenerationInProgressError) {
          // A render is already running — don't offer a retry that re-fails.
          reportGenerationFailure("front", error.message, isChatMode, false);
          return;
        } else if (error instanceof ValidationError) {
          reportGenerationFailure(
            "front",
            error.message,
            isChatMode,
            isPromptValidationError(error)
          );
          return;
        } else if (error instanceof ServerError) {
          reportGenerationFailure(
            "front",
            error.message,
            isChatMode,
            true,
            error.creditRecovery
          );
          return;
        } else {
          // Generic error
          const errorMessage =
            error instanceof Error
              ? error.message
              : GENERIC_GENERATION_FAILURE_MESSAGE;
          reportGenerationFailure("front", errorMessage, isChatMode);
          return;
        }
      }
    },
    [
      store,
      stateRef,
      invalidateCredits,
      onImageComplete,
      contextIsChatMode,
      reportGenerationFailure,
      identityEpochRef,
    ]
  );

  // Generate back image only (step 4)
  const generateBackImage = useCallback(
    async (
      isChatMode?: boolean,
      isRetryAfterRecovery?: boolean,
      designSpecOverride?: DesignSpec,
      options?: DesignRenderExecutionOptions
    ) => {
      const identityEpoch = identityEpochRef.current;
      const run = generationRunRef.current;
      const currentState = useCreatePageStore.getState();
      const owningProjectActionEpoch = currentState.projectActionEpoch;

      try {
        const formData = currentState.form.data;
        const designSpec = createDesignSpecSnapshot(
          designSpecOverride ??
            compileCurrentDesignSpec(currentState.agent.designBrief, formData, {
              hasFrontImage: Boolean(currentState.generation.images.front.url),
              hasBackImage: Boolean(currentState.generation.images.back.url),
            })
        );
        const sourceFrontPath = currentState.generation.images.front.path;
        const backRenderSpec =
          designSpec && sourceFrontPath
            ? createDesignSpecSnapshot({
                ...designSpec,
                renderSource: { frontImagePath: sourceFrontPath },
              })
            : designSpec;
        const result = await generateBackImageService({
          backPrompt: formData.backPrompt || "",
          baseImagePath: currentState.generation.images.front.path!,
          agentSessionId: currentState.agent.sessionId,
          designSpec: backRenderSpec ?? undefined,
          isModificationMode:
            options?.regenerateBackFromFront === true
              ? false
              : currentState.ui.modification.isModificationMode,
          uploadedImages: formData.uploadedBackImages || [],
          uploadedImageUrls: formData.uploadedBackImageUrls || [],
          isUploadPreset: formData.isUploadPreset,
          originalBackPrompt: formData.backPrompt || undefined,
          frontPrompt: formData.prompt || undefined, // Front prompt for back ticket creation
          modestyPreference: formData.modestyPreference,
          formData: {
            length: formData.length,
            sleeve: formData.sleeve,
            sleeveAddons: formData.sleeveAddons,
            neckline: formData.neckline,
            waistline: formData.waistline,
            silhouette: formData.silhouette,
            fabric: formData.fabric,
            color: formData.color,
            colorHex: formData.colorHex,
            preset_type: formData.preset_type,
          },
        });
        const renderedDesignSpec = createDesignSpecSnapshot(
          result.designSpec
            ? {
                ...result.designSpec,
                renderSource: backRenderSpec?.renderSource,
              }
            : backRenderSpec
        );

        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        ) {
          return;
        }

        // Convert variations to state format
        const variations = result.variations.map(v => ({
          imageUrl: v.imageUrl,
          imagePath: v.imagePath,
        }));

        // Update state with variations and warnings
        store.generateBackSuccess(
          {
            variations,
            designSpec: renderedDesignSpec,
            warnings: result.warnings || [],
            partial_failure: result.partialFailure || false,
          },
          isChatMode
        );

        // Show the new back render in the transcript immediately, decoupled from
        // the follow-up review turn (see the front path above for rationale).
        if (isChatMode && variations.length > 0) {
          const imageUrl = variations[0]?.imageUrl;
          if (imageUrl) {
            const variationUrls = variations.map(v => v.imageUrl);
            store.addMessage({
              role: "assistant",
              content: "",
              imageUrl,
              imageUrls: variationUrls,
              imageSide: "back",
              selectedIndex: 0,
            });
            if (onImageComplete && !options?.suppressAgentReview) {
              onImageComplete(
                "back",
                imageUrl,
                variationUrls,
                false,
                result.imageReviewToken
              );
            }
          }
        }

        // Store ticket IDs from API response - tickets are created server-side
        if (variations.length > 0) {
          for (let i = 0; i < result.variations.length; i++) {
            const ticketId = result.variations[i]?.ticketId;
            if (ticketId) {
              store.setVariationTicketId(i, ticketId);
              logger.info(`Ticket ID stored for back variation ${i}`, {
                component: "CreatePageActions",
                metadata: {
                  ticketId,
                  variationIndex: i,
                  totalVariations: variations.length,
                },
              });
            } else {
              logger.warn(`No ticket ID returned for back variation ${i}`, {
                component: "CreatePageActions",
                metadata: {
                  variationIndex: i,
                  totalVariations: variations.length,
                },
              });
            }
          }

          // Set main ticketId from first variation (which is auto-displayed).
          // Read it from the API response, not stateRef.current: the ref has not
          // been synced with the Zustand writes above, so it still holds the
          // front generation's ticket ids.
          const firstVariationTicketId = result.variations[0]?.ticketId;
          if (firstVariationTicketId) {
            store.setTicketId(firstVariationTicketId);
            logger.info("Set main ticketId from first back variation", {
              component: "CreatePageActions",
              metadata: {
                ticketId: firstVariationTicketId,
                variationIndex: 0,
              },
            });
          }
        }

        // Refresh credits after successful generation to sync with server (credit was deducted)
        // This is the single source of truth update after generation
        invalidateCredits().catch(error => {
          logger.warn("Failed to refresh credits after back generation", {
            component: "CreatePageActions",
            error: error instanceof Error ? error.message : String(error),
          });
        });
      } catch (error) {
        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        )
          return;
        // Only prompt-field validation can be repaired from chat context.
        const canRecover =
          isPromptValidationError(error) &&
          (isChatMode ?? contextIsChatMode) &&
          !isRetryAfterRecovery;

        if (canRecover) {
          const state = useCreatePageStore.getState();
          const messages = state.agent.conversation.messages;
          if (messages.length > 0) {
            try {
              const recoveredPrompt = await recoverPromptFromChat({
                messages: messages.map(m => ({
                  role: m.role,
                  content: m.content,
                })),
                formState: state.form.data,
                side: "back",
              });
              if (
                generationRunRef.current !== run ||
                identityEpochRef.current !== identityEpoch ||
                !isProjectActionCurrent(owningProjectActionEpoch)
              ) {
                return;
              }
              store.updateFormData({ backPrompt: recoveredPrompt });
              logger.info(
                "Recovered back prompt from chat, retrying back generation",
                {
                  component: "CreatePageActions",
                  metadata: { promptLength: recoveredPrompt.length },
                }
              );
              return generateBackImage(
                isChatMode,
                true,
                designSpecOverride,
                options
              );
            } catch (recoveryError) {
              logger.warn(
                "Back prompt recovery failed, showing validation error",
                {
                  component: "CreatePageActions",
                  metadata: {
                    error:
                      recoveryError instanceof Error
                        ? recoveryError.message
                        : String(recoveryError),
                  },
                }
              );
            }
          }
        }

        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        ) {
          return;
        }

        // Handle service errors
        if (error instanceof InsufficientCreditsError) {
          if (typeof window !== "undefined" && window.localStorage) {
            window.localStorage.removeItem("lastGenerationTime");
          }
          // Insufficient credits: revert to previous step and show modal. Same
          // anonymous split as the front path above.
          const stepAtWall = useCreatePageStore.getState().form.currentStep;
          store.setStep(3);
          store.setLoading(false);
          store.setCreditModalDismissed(false);
          store.setShowCreditModal(
            error.wall === "anonymous_free_render_spent"
              ? "anon_unlock"
              : "generate_back",
            stepAtWall
          );
          // Refresh credits to sync with server state
          invalidateCredits().catch(() => {});
          return;
        } else if (error instanceof RateLimitError) {
          const errorMessage = error.retryAfter
            ? `${error.message} Try again in ${error.retryAfter} seconds.`
            : error.message;
          reportGenerationFailure("back", errorMessage, isChatMode);
          return;
        } else if (error instanceof GenerationInProgressError) {
          // A render is already running — don't offer a retry that re-fails.
          reportGenerationFailure("back", error.message, isChatMode, false);
          return;
        } else if (error instanceof ValidationError) {
          reportGenerationFailure(
            "back",
            error.message,
            isChatMode,
            isPromptValidationError(error)
          );
          return;
        } else if (error instanceof ServerError) {
          reportGenerationFailure(
            "back",
            error.message,
            isChatMode,
            true,
            error.creditRecovery
          );
          return;
        } else {
          // Generic error
          const errorMessage =
            error instanceof Error
              ? error.message
              : GENERIC_GENERATION_FAILURE_MESSAGE;

          reportGenerationFailure("back", errorMessage, isChatMode);
          return;
        }
      }
    },
    [
      store,
      invalidateCredits,
      onImageComplete,
      contextIsChatMode,
      reportGenerationFailure,
      identityEpochRef,
    ]
  );

  // Image generation actions
  const generateImages = useCallback(
    async (
      targetStep?: number,
      isChatMode?: boolean,
      designSpecOverride?: DesignSpec,
      options?: DesignRenderExecutionOptions
    ) => {
      // Guard against multiple simultaneous calls
      if (isGeneratingRef.current) {
        logger.debug(
          "Generation already in progress, ignoring duplicate call",
          {
            component: "CreatePageActions",
          }
        );
        return;
      }

      // Wait for credits to load or finish refetching before allowing generation
      // This prevents generation attempts before we know the credit balance
      // Note: Credit purchase redirects are handled by useCreditPurchaseGate at the page level,
      // which blocks the page until credits are refreshed
      if (session && (creditsLoading || creditsFetching)) {
        logger.debug(
          "Credits still loading/fetching, waiting before generation",
          {
            component: "CreatePageActions",
            creditsLoading,
            creditsFetching,
          }
        );
        isGeneratingRef.current = false;
        // Don't show error or block - just silently wait
        // The button will be disabled in the UI while credits are loading
        return;
      }

      // Set flag immediately to prevent multiple calls
      isGeneratingRef.current = true;
      const run = generationRunRef.current + 1;
      generationRunRef.current = run;
      const identityEpoch = identityEpochRef.current;
      const owningProjectActionEpoch =
        useCreatePageStore.getState().projectActionEpoch;

      // Track if generation was successful - if so, SUCCESS actions will handle loading state
      let generationSuccessful = false;

      try {
        // Read the live store snapshot. This is important for upload flows that
        // call updateFormData immediately before generation starts.
        const currentState = useCreatePageStore.getState();
        const stepToUse = targetStep || currentState.form.currentStep;
        if (
          stepToUse === 4 &&
          currentState.generation.images.back.url &&
          planDesignChanges(currentState).steps[0]?.side === "front"
        ) {
          const savedFront = currentState.generation.images.front.designSpec;
          const currentDraft = compileCurrentDesignSpec(
            currentState.agent.designBrief,
            currentState.form.data,
            { hasFrontImage: true, hasBackImage: true }
          );
          const requestedFrontDiffers = Boolean(
            savedFront &&
            designSpecOverride &&
            getSideRelevantDesignChanges(
              diffDesignSpecFields(savedFront, designSpecOverride),
              "front"
            ).length
          );
          const requestMatchesUnappliedDraft = Boolean(
            designSpecOverride &&
            currentDraft &&
            !getSideRelevantDesignChanges(
              diffDesignSpecFields(designSpecOverride, currentDraft),
              "front"
            ).length
          );
          // A current draft that changes Front needs its own render first. An
          // older signed chat action can still refresh Back: its old shared
          // fields are replaced with the saved Front at dispatch below.
          if (
            !designSpecOverride ||
            (requestedFrontDiffers && requestMatchesUnappliedDraft)
          ) {
            store.setError(
              "Update the front preview before creating the back from it."
            );
            return;
          }
        }

        // Update journey stage to design
        updateJourneyStage("design", stepToUse);

        // Determine journey path if this is first generation
        const journey = getJourneyContext();
        if (journey && journey.journey_path === "unknown") {
          // Credit-grant timing does not define the journey taxonomy. Default
          // unknown journeys to the ordinary full-creation path.
          updateJourneyPath("full_creation");
        }

        if (isChatMode && shouldInspectGeneratePrompt(searchParams)) {
          try {
            const formData = currentState.form.data;
            const designSpec =
              designSpecOverride ??
              compileCurrentDesignSpec(
                currentState.agent.designBrief,
                formData,
                {
                  hasFrontImage: Boolean(
                    currentState.generation.images.front.url
                  ),
                  hasBackImage: Boolean(
                    currentState.generation.images.back.url
                  ),
                }
              );
            const inspection =
              stepToUse === 4
                ? await inspectBackImagePrompt({
                    backPrompt: formData.backPrompt || "",
                    baseImagePath:
                      currentState.generation.images.front.path || "",
                    agentSessionId: currentState.agent.sessionId,
                    designSpec,
                    isModificationMode:
                      currentState.ui.modification.isModificationMode,
                    uploadedImages: formData.uploadedBackImages || [],
                    uploadedImageUrls: formData.uploadedBackImageUrls || [],
                    isUploadPreset: formData.isUploadPreset,
                    originalBackPrompt: formData.backPrompt || undefined,
                    frontPrompt: formData.prompt || undefined,
                    modestyPreference: formData.modestyPreference,
                    formData: {
                      length: formData.length,
                      sleeve: formData.sleeve,
                      sleeveAddons: formData.sleeveAddons,
                      neckline: formData.neckline,
                      waistline: formData.waistline,
                      silhouette: formData.silhouette,
                      fabric: formData.fabric,
                      color: formData.color,
                      colorHex: formData.colorHex,
                      preset_type: formData.preset_type,
                    },
                  })
                : await inspectFrontImagePrompt({
                    prompt: formData.prompt,
                    agentSessionId: currentState.agent.sessionId,
                    designSpec,
                    preset_type: formData.preset_type ?? undefined,
                    color: formData.color,
                    colorHex: formData.colorHex,
                    length: formData.length,
                    sleeve: formData.sleeve,
                    sleeveAddons: formData.sleeveAddons,
                    neckline: formData.neckline,
                    waistline: formData.waistline,
                    silhouette: formData.silhouette,
                    fabric: formData.fabric,
                    isModificationMode:
                      currentState.ui.modification.isModificationMode,
                    uploadedImages: formData.uploadedFrontImages || [],
                    uploadedImageUrls: formData.uploadedFrontImageUrls || [],
                    isUploadPreset: formData.isUploadPreset,
                    enableCrazyMode: formData.enableCrazyMode || false,
                    originalPrompt: formData.prompt || undefined,
                    modestyPreference: formData.modestyPreference,
                  });

            if (
              generationRunRef.current !== run ||
              identityEpochRef.current !== identityEpoch ||
              !isProjectActionCurrent(owningProjectActionEpoch)
            ) {
              return;
            }

            const promptPayload = inspection.promptPayload;
            logger.info("Generate prompt inspection completed", {
              component: "CreatePageActions",
              action: "inspectGeneratePrompt",
              metadata: {
                side: promptPayload.side,
                promptLength: promptPayload.finalPrompt.length,
              },
            });
            store.addMessage({
              role: "assistant",
              content: formatPromptInspectionMessage({
                side: promptPayload.side,
                requestBody: inspection.requestBody,
                systemInstruction: promptPayload.systemInstruction,
                finalPrompt: promptPayload.finalPrompt,
              }),
            });
          } catch (inspectionError) {
            if (
              generationRunRef.current !== run ||
              identityEpochRef.current !== identityEpoch ||
              !isProjectActionCurrent(owningProjectActionEpoch)
            ) {
              return;
            }
            const message =
              inspectionError instanceof Error
                ? inspectionError.message
                : "Failed to inspect generate prompt";
            store.addMessage({
              role: "assistant",
              content: `Generate prompt inspection failed: ${message}`,
            });
            store.setAgentError(message);
          }
          generationSuccessful = false;
          return;
        }

        if (!session) {
          // Auth may still be hydrating (server-cookie session verified via an
          // async fetch). During that window `session` is null even though the
          // HttpOnly cookie is valid, so acting here would strand a logged-in
          // user's in-progress design. Wait for hydration instead; the generate
          // presets are disabled while auth is loading, so this is a brief
          // no-op rather than a dropped click.
          if (rolesLoading) {
            logger.debug("Auth still hydrating, deferring generation gate", {
              component: "CreatePageActions",
            });
            isGeneratingRef.current = false;
            return;
          }

          // This used to save a savepoint and router.push("/login") — ejecting
          // the customer from a conversation they were in the middle of, at the
          // exact moment they asked to see their dress. It was the single
          // largest drop in the funnel. Now we give them an identity in place
          // and keep going; nothing leaves the screen and there is nothing to
          // restore afterwards.
          const anonymous = await ensureAnonymousSession();
          if (
            generationRunRef.current !== run ||
            identityEpochRef.current !== identityEpoch ||
            !isProjectActionCurrent(owningProjectActionEpoch)
          ) {
            return;
          }
          if (!anonymous.ok) {
            // Anonymous sign-in is unavailable (switch off in the project, or
            // the mint failed). Fall back to the old behaviour rather than
            // leaving the click silently dead.
            store.setStep(1);
            FormPersistenceService.saveState({
              data: prepareSavePointPayload(currentState, 1),
              source: "auth_redirect",
            });
            isGeneratingRef.current = false;
            const currentUrl = searchParams.toString()
              ? `${pathname}?${searchParams.toString()}`
              : pathname;
            redirectToLogin(router, currentUrl);
            return;
          }

          // Bring React state in line with the cookies we just received. The
          // request below would succeed without this — cookies are already
          // stored — but every later render would still believe it is signed
          // out and re-raise the gate.
          await refreshSession().catch(() => {});
          if (
            generationRunRef.current !== run ||
            identityEpochRef.current !== identityEpoch ||
            !isProjectActionCurrent(owningProjectActionEpoch)
          ) {
            return;
          }
        }

        // Check credits only if we have a session and credits have finished
        // loading. Don't check if credits are still loading (we already
        // returned above). Wait for roles to load before checking admin status.
        //
        // Anonymous callers are deliberately exempt: their free render is
        // granted server-side, inside the same transaction that spends it, so
        // their client-visible balance is legitimately 0 right up until the
        // render happens. Pre-checking here would refuse the one request that
        // was going to succeed. The server answers instead — either it renders,
        // or it returns 402 with credit_wall: "anonymous_free_render_spent",
        // which raises the sign-up wall below.
        const creditCheck =
          session && !isAnonymous && !rolesLoading && !roles.admin
            ? hasSufficientCredits(1)
            : true;
        // If credits are still loading (null), wait - we already checked creditsLoading above
        if (creditCheck === null) {
          logger.debug("Credits still loading, waiting before generation", {
            component: "CreatePageActions",
          });
          isGeneratingRef.current = false;
          return;
        }
        // If credits are insufficient (false), show modal
        if (creditCheck === false) {
          // Revert step to before generation attempt
          const stepToUse = targetStep || currentState.form.currentStep;

          // Save state BEFORE reverting step - preserve the original step where user ran out of credits
          const formDataToSave = prepareSavePointPayload(currentState);

          logger.info("Saving state before showing credit modal", {
            component: "useGenerationActions",
            action: "generateImages",
            originalStep: currentState.form.currentStep,
            stepToUse,
            savedStep: formDataToSave.currentStep,
            isChatMode,
            hasImages: !!(
              currentState.generation.images.front.url ||
              currentState.generation.images.front.path
            ),
          });

          FormPersistenceService.saveState({
            data: formDataToSave,
            source: "credit_modal_shown",
          });

          // Only revert step if NOT in chat mode (steps are less relevant in chat)
          if (!isChatMode) {
            if (stepToUse === 2) {
              store.setStep(1);
            } else if (stepToUse === 4) {
              store.setStep(3);
            }
          }

          // Determine context based on what we're generating
          const context =
            stepToUse === 2 || stepToUse === 1
              ? "generate_front"
              : "generate_back";

          // Reset dismissed flag so modal can show when user explicitly tries to generate
          store.setCreditModalDismissed(false);
          store.setShowCreditModal(context, stepToUse);
          isGeneratingRef.current = false;
          return;
        }

        // Only an attempt that has passed the identity and credit handoffs
        // should start the generation cooldown. A blocked attempt must leave
        // Generate available so the customer can reopen the credit flow.
        const now = Date.now();
        const lastGenerationTime =
          typeof window !== "undefined" && window.localStorage
            ? localStorage.getItem("lastGenerationTime")
            : null;
        const timeSinceLastGeneration = lastGenerationTime
          ? now - parseInt(lastGenerationTime)
          : Infinity;

        if (
          timeSinceLastGeneration < CLIENT_RATE_LIMITS.GENERATE_BUTTON_COOLDOWN
        ) {
          const remainingTime =
            CLIENT_RATE_LIMITS.GENERATE_BUTTON_COOLDOWN -
            timeSinceLastGeneration;

          // Revert step if we were called from handleNext (which advances step first)
          if (targetStep === 2) {
            store.setStep(1);
          } else if (targetStep === 4) {
            store.setStep(3);
          }

          store.setError(
            `Please wait ${Math.ceil(remainingTime / 1000)} seconds before generating again.`
          );
          store.setGenerationBlocked(true, remainingTime);

          setTimeout(() => {
            if (
              generationRunRef.current !== run ||
              identityEpochRef.current !== identityEpoch ||
              !isProjectActionCurrent(owningProjectActionEpoch)
            ) {
              return;
            }
            store.setGenerationBlocked(false, 0);
            store.clearError();
          }, remainingTime);

          isGeneratingRef.current = false;
          return;
        }

        if (typeof window !== "undefined" && window.localStorage) {
          localStorage.setItem("lastGenerationTime", now.toString());
        }

        logger.info("Starting image generation", {
          component: "CreatePageActions",
          metadata: {
            stepToUse,
            isChatMode: !!isChatMode,
            currentStep: currentState.form.currentStep,
            triggerSource: isChatMode ? "chat_mode" : "form_mode",
          },
        });

        // Mirrors the credit-modal mapping above: only steps 1-2 are the front.
        const side: "front" | "back" =
          stepToUse === 1 || stepToUse === 2 ? "front" : "back";
        const imagesAtDispatch =
          useCreatePageStore.getState().generation.images;
        const refreshesExistingPreview = Boolean(imagesAtDispatch[side].url);
        const renderOptions =
          side === "back" &&
          refreshesExistingPreview &&
          projectSavedBackStaleness(imagesAtDispatch).stale
            ? { ...options, regenerateBackFromFront: true }
            : options;
        const renderDesignSpecOverride =
          side === "back" && renderOptions?.regenerateBackFromFront
            ? getBackDesignSpecForLatestFront(
                imagesAtDispatch,
                designSpecOverride ??
                  compileCurrentDesignSpec(
                    currentState.agent.designBrief,
                    currentState.form.data,
                    {
                      hasFrontImage: Boolean(imagesAtDispatch.front.url),
                      hasBackImage: Boolean(imagesAtDispatch.back.url),
                    }
                  )
              )
            : designSpecOverride;
        if (
          side === "back" &&
          renderOptions?.regenerateBackFromFront &&
          !renderDesignSpecOverride
        ) {
          store.setError("Save the front preview before updating the back.");
          return;
        }

        store.generateStart(stepToUse, isChatMode, {
          // Keep both old previews visible while an in-place render runs. A
          // failed Front revision must not erase its existing Back either.
          preserveExistingPreviews: refreshesExistingPreview,
        });
        armGenerationWatchdog(
          side,
          isChatMode,
          run,
          identityEpoch,
          owningProjectActionEpoch
        );
        if (stepToUse === 2) {
          // Generate front image only
          await generateFrontImage(
            isChatMode,
            false,
            renderDesignSpecOverride ?? undefined,
            renderOptions
          );
          // Mark generation as successful - SUCCESS actions will handle loading state
          generationSuccessful = true;
        } else if (stepToUse === 4) {
          // Generate back image only
          await generateBackImage(
            isChatMode,
            false,
            renderDesignSpecOverride ?? undefined,
            renderOptions
          );
          generationSuccessful = true;
        } else {
          // generateStart has already flipped the UI into its loading state, so
          // a step with no generator behind it would spin forever while the
          // success path reported a render that never happened. Only steps 2
          // and 4 have generators; anything else is a caller bug, so fail the
          // turn loudly and hand back the retry affordance.
          logger.error("Generation dispatched with an unroutable step", {
            component: "CreatePageActions",
            metadata: {
              stepToUse,
              targetStep,
              currentStep: currentState.form.currentStep,
              isChatMode: !!isChatMode,
            },
          });
          reportGenerationFailure(
            side,
            GENERIC_GENERATION_FAILURE_MESSAGE,
            isChatMode
          );
        }
      } catch (error) {
        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        ) {
          return;
        }
        logger.error("Image generation failed", {
          component: "CreatePageActions",
          metadata: {
            error: error instanceof Error ? error.message : String(error),
          },
        });
        reportGenerationFailure(
          targetStep === 4 ? "back" : "front",
          GENERIC_GENERATION_FAILURE_MESSAGE,
          isChatMode
        );
      } finally {
        if (
          generationRunRef.current !== run ||
          identityEpochRef.current !== identityEpoch ||
          !isProjectActionCurrent(owningProjectActionEpoch)
        ) {
          return;
        }
        clearGenerationWatchdog();
        // Only set loading to false if generation failed
        // Successful generation is handled by GENERATE_FRONT_SUCCESS or GENERATE_BACK_SUCCESS
        if (!generationSuccessful) {
          store.setLoading(false);
        }
        // Clear the generation flag
        isGeneratingRef.current = false;
      }
    },
    [
      session,
      // Both flip the moment an anonymous session is minted mid-callback.
      // Omitting them freezes the callback on "signed out", so the very next
      // generate re-mints and re-runs the gate it just passed.
      isAnonymous,
      refreshSession,
      roles,
      rolesLoading,
      hasSufficientCredits,
      creditsLoading,
      creditsFetching,
      router,
      pathname,
      searchParams,
      store,
      isGeneratingRef,
      generateFrontImage,
      generateBackImage,
      reportGenerationFailure,
      armGenerationWatchdog,
      clearGenerationWatchdog,
      identityEpochRef,
    ]
  );

  return {
    generateImages,
  };
}
